import * as React from 'react'
import { useMemo } from 'react'
import {
  ColumnDef,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  TableMeta,
  useReactTable,
  ExpandedState,
  getExpandedRowModel,
} from '@tanstack/react-table'
import { logger } from '@magnetar/utils'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import TodayAdPlayCount from '@/components/TodayAdPlayCount'
import { Clinic } from '@magnetar/admin-api-interfaces'
import { useBroadcastDeviceDialog } from '@/stores/useBroadcastDeviceDialog'
import { useDeviceOnlineStatus } from '@/hooks/useDeviceOnlineStatus'
import {
  getDeviceConnectionState as getConnectionState,
  getConnectionDisplay,
  formatLastConnectedAt,
} from '@/utils/deviceConnectionStatus'

type BroadcastDeviceItem = NonNullable<Clinic['broadcast_devices']>[number]

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  supplierId: string | number
  meta?: {
    reloadData: () => void
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  supplierId,
  meta,
}: DataTableProps<TData, TValue>) {
  const [searchValue, setSearchValue] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'clinic_id', desc: false }])
  const [expanded, setExpanded] = React.useState<ExpandedState>(true)
  const { openDialog } = useBroadcastDeviceDialog()

  // 收集所有設備 ID
  const allDeviceIds = useMemo(() => {
    const ids: number[] = []
    ;(data as Clinic[]).forEach((clinic) => {
      if (clinic.broadcast_devices) {
        clinic.broadcast_devices.forEach((device: BroadcastDeviceItem) => {
          ids.push(device.broadcast_device_id)
        })
      }
    })
    return ids
  }, [data])

  // 使用通用的設備在線狀態 Hook
  const { onlineDeviceIds, deviceLastConnectedMap } = useDeviceOnlineStatus(allDeviceIds)

  // 检查设备是否在线
  const isDeviceOnline = (broadcastDeviceId: number): boolean => {
    return onlineDeviceIds.includes(broadcastDeviceId)
  }

  // 獲取設備連線狀態（使用共用工具函式）
  const getDeviceConnectionState = (broadcastDeviceId: number) => {
    const online = isDeviceOnline(broadcastDeviceId)
    const lastConnectedAt = deviceLastConnectedMap[broadcastDeviceId] || null

    return getConnectionState(online, lastConnectedAt)
  }

  // Global filter：搜尋診所名稱 / alias / tags（substring，case-insensitive）
  const globalFilterFn: FilterFn<TData> = (row, _columnId, filterValue) => {
    const q = String(filterValue ?? '').trim().toLowerCase()
    if (!q) return true
    const clinic = row.original as Clinic
    const fields = [clinic.clinic_name, clinic.alias]
    if (fields.some((f) => f?.toLowerCase().includes(q))) return true
    const tags = (clinic.tags ?? []) as string[]
    return tags.some((t) => t.toLowerCase().includes(q))
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    globalFilterFn,
    state: {
      sorting,
      expanded,
      globalFilter: searchValue,
    },
    meta: meta as TableMeta<TData>,
  })

  return (
    <div>
      <div className="flex items-center py-4">
        <Input
          placeholder="搜尋診所名稱、標籤..."
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          className="max-w-sm"
        />
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {/* 添加空的表頭單元格，對應展開按鈕列 */}
                <TableHead className="w-10"></TableHead>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={(header.column.columnDef.meta as { className?: string })?.className}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                // 獲取原始資料
                const clinic = row.original as Clinic
                const hasDevices = clinic.broadcast_devices && clinic.broadcast_devices.length > 0

                return (
                  <React.Fragment key={row.id}>
                    <TableRow data-state={row.getIsSelected() && 'selected'}>
                      {/* 添加展開按鈕 */}
                      <TableCell className="w-10">
                        {hasDevices && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              row.toggleExpanded()
                            }}
                          >
                            {row.getIsExpanded() ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>

                      {/* 原始資料欄位 */}
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={(cell.column.columnDef.meta as { className?: string })?.className}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>

                    {/* 展開的子表格 - 廣播裝置列表 */}
                    {row.getIsExpanded() && hasDevices && (
                      <TableRow>
                        <TableCell colSpan={columns.length + 1} className="p-0">
                          <div className="bg-muted/50 p-4">
                            <h3 className="text-sm font-medium mb-2">廣播裝置列表</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {clinic.broadcast_devices.map((device: BroadcastDeviceItem) => (
                                <div
                                  key={device.broadcast_device_id}
                                  className="bg-card border rounded-md p-3 hover:border-primary cursor-pointer transition-colors"
                                  onClick={() => {
                                    logger.log(device)
                                    openDialog(device)
                                  }}
                                >
                                  {/* 連線狀態與播放次數區塊 */}
                                  <div className="flex gap-2 mb-2">
                                    {(() => {
                                      const { state, lastConnectedAt } = getDeviceConnectionState(
                                        device.broadcast_device_id,
                                      )
                                      const display = getConnectionDisplay(state)

                                      return (
                                        <div
                                          className={`flex-1 flex flex-col items-center justify-center border rounded px-2 py-1 ${
                                            display.showWarning
                                              ? 'border-orange-300 bg-orange-50'
                                              : ''
                                          }`}
                                        >
                                          <div className="flex items-center gap-1 mb-1">
                                            <span
                                              className={`inline-block w-2 h-2 rounded-full ${display.dotClass}`}
                                              aria-label={display.text}
                                            />
                                            <span className="text-xs text-muted-foreground">
                                              連線狀態
                                            </span>
                                            {display.showWarning && (
                                              <AlertTriangle className="h-3 w-3 text-orange-500" />
                                            )}
                                          </div>
                                          <span className={`text-sm font-medium ${display.textClass}`}>
                                            {display.text}
                                          </span>
                                          {state !== 'connected' && (
                                            <span className="text-xs text-muted-foreground mt-1">
                                              {lastConnectedAt
                                                ? `最後連線: ${formatLastConnectedAt(lastConnectedAt)}`
                                                : '無連線記錄'}
                                            </span>
                                          )}
                                        </div>
                                      )
                                    })()}
                                    <TodayAdPlayCount
                                      broadcastDeviceId={device.broadcast_device_id}
                                      supplierId={Number(supplierId)}
                                    />
                                  </div>
                                  {/* 原本內容 */}
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium">{device.name}</span>
                                    <Badge
                                      variant={device.is_active ? 'default' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {device.is_active ? '啟用中' : '停用'}
                                    </Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    <div className="flex justify-between mb-1">
                                      <span>ID:</span>
                                      <span>{device.broadcast_device_id}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span>類型:</span>
                                      <span>{device.broadcast_type}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
